import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { DataTable } from '@/components/ui/data-table';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';

export default function QuickBooksPage() {
  const qc = useQueryClient();
  const [authorizeUrl, setAuthorizeUrl] = useState(null);
  const [syncErrors, setSyncErrors] = useState([]);
  const [results, setResults] = useState([]);

  const connQ = useQuery({
    queryKey: ['qb-connection'],
    queryFn: async () => {
      const r = await api('/api/v1/qb/connection');
      if (r.status !== 200) throw new Error(r.data?.error ?? 'Failed to load QB connection');
      return r.data;
    },
  });

  useEffect(() => {
    if (connQ.isError) toast.error(connQ.error?.message ?? 'Failed to load QuickBooks');
  }, [connQ.isError, connQ.error]);

  const statusLabel = connQ.data?.statusLabel ?? 'Beta';
  const connection = connQ.data?.connection;
  const connectionSummary = connection
    ? `${connection.status} · realm ${connection.realmId || '—'} · tokens ${
        connection.hasTokens ? 'yes' : 'no'
      }`
    : 'Not connected';

  const connect = async () => {
    const r = await api('/api/v1/qb/connect', { method: 'POST', body: {} });
    if (r.data?.authorizeUrl) {
      setAuthorizeUrl(r.data.authorizeUrl);
      window.open(r.data.authorizeUrl, '_blank', 'noopener,noreferrer');
    }
    if (r.data?.error) toast.message(r.data.error);
    qc.invalidateQueries({ queryKey: ['qb-connection'] });
  };

  const disconnect = async () => {
    await api('/api/v1/qb/disconnect', { method: 'POST', body: {} });
    setAuthorizeUrl(null);
    setSyncErrors([]);
    setResults([]);
    toast.success('Disconnected');
    qc.invalidateQueries({ queryKey: ['qb-connection'] });
  };

  const sync = async () => {
    const r = await api('/api/v1/qb/sync/invoices', { method: 'POST', body: {} });
    setSyncErrors(r.data?.errors ?? []);
    setResults(r.data?.results ?? []);
    toast.message(`Sync finished — ${(r.data?.results ?? []).length} row(s)`);
  };

  const columns = [
    {
      key: 'invoiceNumber',
      label: 'Invoice',
      className: 'font-mono',
      render: (v, row) => v ?? row.invoiceId?.slice(0, 8) ?? '—',
    },
    {
      key: 'qbSyncStatus',
      label: 'Status',
      render: (v) => <StatusPill status={v} />,
    },
    {
      key: 'qbInvoiceId',
      label: 'QuickBooks ID',
      className: 'font-mono text-xs',
      render: (v) => v || '—',
    },
    {
      key: 'error',
      label: 'Error',
      className: 'text-xs text-destructive',
      render: (v) => v || '—',
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title={`QuickBooks (${statusLabel})`}
          description="Sync invoices and payments with QuickBooks Online"
          icon="BookOpen"
          metrics={[
            { label: 'Connection', value: connection ? 'Live' : 'Off' },
            { label: 'Sync rows', value: results.length },
            { label: 'Errors', value: syncErrors.length },
          ]}
        />
        <Card className="border-l-4 border-l-blue-500 bg-blue-50 dark:bg-blue-900/10">
          <CardHeader className="flex flex-row items-start">
            <Icons.Info className="h-5 w-5 text-blue-600 dark:text-blue-500 mr-3" />
            <div>
              <CardTitle className="text-base">Sandbox Environment</CardTitle>
              <CardDescription className="mt-1">
                {connectionSummary}. QuickBooks (Beta) — do not market as fully integrated until
                Intuit production approval.
              </CardDescription>
            </div>
          </CardHeader>
        </Card>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={connect}>
            <Icons.Link className="h-4 w-4 mr-2" />
            Connect Sandbox
          </Button>
          <Button variant="outline" onClick={sync}>
            <Icons.RefreshCw className="h-4 w-4 mr-2" />
            Sync Invoices
          </Button>
          <Button variant="outline" onClick={disconnect}>
            <Icons.Unlink className="h-4 w-4 mr-2" />
            Disconnect
          </Button>
          <Button variant="ghost" onClick={() => qc.invalidateQueries({ queryKey: ['qb-connection'] })}>
            Refresh
          </Button>
        </div>
        {authorizeUrl ? (
          <p className="text-sm text-muted-foreground">
            If the popup was blocked, open:{' '}
            <a href={authorizeUrl} target="_blank" rel="noreferrer" className="underline">
              Intuit authorize
            </a>
          </p>
        ) : null}
        {syncErrors.length > 0 && (
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-base text-destructive">Sync errors</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 text-sm text-destructive space-y-1">
                {syncErrors.map((e) => (
                  <li key={e}>{e}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Sync Results</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={columns}
              data={results}
              emptyMessage="Run sync to see results"
            />
          </CardContent>
        </Card>
      </div>
);
}
